/**
 * ReSTIR DI の点光源と ReSTIR GI の拡散をまとめてかけるデノイザ。
 * 時間再投影と geometry-aware A-trous を WebGL2 の raster pass だけで行い、
 * オフ / 時間のみ / SVGF / SVGF sharp / RELAX を切り替える。
 */

import * as THREE from 'three';
import { FullScreenQuad } from 'three/addons/postprocessing/Pass.js';
import type { GraphicsQuality } from './graphics-quality';
import type { GBuffer } from './gbuffer';

export type RestirDenoiserDebugView = 'off' | 'raw' | 'temporal' | 'variance' | 'denoised';

export const RESTIR_DENOISER_MODES = ['off', 'temporal', 'svgf', 'svgf-sharp', 'relax'] as const;
export type RestirDenoiserMode = (typeof RESTIR_DENOISER_MODES)[number];
export const DEFAULT_RESTIR_DENOISER_MODE: RestirDenoiserMode = 'temporal';

export interface RestirDenoiserModeParams {
  atrous: boolean;
  feedbackFiltered: boolean;
  rectifySigma: number;
  antiLag: number;
  spatialFillOnMiss: number;
  sigmaDepth: number;
  sigmaNormal: number;
  sigmaLumDiffuse: number;
  sigmaLumSpecular: number;
  maxStep: number;
  historyBlurBoost: number;
}

export const DIFFUSE_MAX_HISTORY = 32;
export const SPECULAR_MAX_HISTORY = 12;
export const FIREFLY_LUMINANCE = 32;
export const HISTORY_NORMAL_DOT_MIN = 0.85;
export const HISTORY_POS_VOXEL_SCALE = 1.5;
export const HISTORY_POS_VIEW_SCALE = 0.015;

const VERT = /* glsl */ `
out vec2 vUv;
void main() {
	vUv = uv;
	gl_Position = vec4(position.xy, 0.0, 1.0);
}
`;

const DENOISE_COMMON = /* glsl */ `
const float PI = 3.14159265359;
const float DIFFUSE_MAX_HISTORY = ${DIFFUSE_MAX_HISTORY.toFixed(1)};
const float SPECULAR_MAX_HISTORY = ${SPECULAR_MAX_HISTORY.toFixed(1)};
const float NORMAL_DOT_MIN = ${HISTORY_NORMAL_DOT_MIN.toFixed(2)};
const float POS_VOXEL_SCALE = ${HISTORY_POS_VOXEL_SCALE.toFixed(2)};
const float POS_VIEW_SCALE = ${HISTORY_POS_VIEW_SCALE.toFixed(3)};

uniform sampler2D depthMap;
uniform sampler2D albedoMetalMap;
uniform sampler2D normalRoughMap;
uniform sampler2D currentPosition;
uniform sampler2D currentNormal;
uniform sampler2D previousPosition;
uniform sampler2D previousNormal;
uniform mat4 sceneViewMatrix;
uniform vec3 cameraPos;
uniform vec2 texel;
uniform float sdfVoxelSize;
uniform float historyValid;

float lightLuminance(vec3 c) { return dot(c, vec3(0.2126, 0.7152, 0.0722)); }

float viewZOf(vec3 worldP) {
	return -(sceneViewMatrix * vec4(worldP, 1.0)).z;
}

float positionLimit(float viewZ) {
	return max(sdfVoxelSize * POS_VOXEL_SCALE, abs(viewZ) * POS_VIEW_SCALE);
}

bool geometryConsistent(vec3 p, vec3 n, vec4 tapPos, vec3 tapN, float viewZ) {
	if (tapPos.w < 0.5) return false;
	if (dot(n, tapN) < NORMAL_DOT_MIN) return false;
	return distance(p, tapPos.xyz) < positionLimit(viewZ);
}

vec3 rectifyColor(vec3 color, float nMin, float nMax, float mean, float variance, float sigmaK) {
	float lum = lightLuminance(color);
	float clamped;
	if (sigmaK < 0.01) {
		float pad = 0.25 * max(nMax - nMin, 0.0) + 1e-4;
		clamped = clamp(lum, max(nMin - pad, 0.0), nMax + pad);
	} else {
		float spread = sqrt(max(variance, 0.0)) * sigmaK;
		clamped = clamp(lum, max(mean - spread, 0.0), mean + spread);
	}
	if (lum < 1e-8) return vec3(0.0);
	return color * (clamped / lum);
}

float antiLagScale(float current, float historyMean, float historyVariance) {
	float sigma = sqrt(max(historyVariance, 1e-6));
	float lag = abs(current - historyMean) / (2.5 * sigma + 1e-3);
	if (lag <= 1.0) return 1.0;
	return 1.0 / (1.0 + lag);
}

vec2 varianceFromMoments(vec4 moments) {
	return vec2(
		max(moments.y - moments.x * moments.x, 0.0),
		max(moments.w - moments.z * moments.z, 0.0)
	);
}
`;

export function luminanceVariance(moment1: number, moment2: number): number {
  return Math.max(moment2 - moment1 * moment1, 0);
}

export function temporalAlpha(historyLength: number, maxHistory: number): number {
  const n = Math.min(Math.max(historyLength, 1), Math.max(maxHistory, 1));
  return 1 / n;
}

export function nextHistoryLength(previous: number, valid: boolean, maxHistory: number): number {
  if (!valid) return 1;
  return Math.min(Math.max(previous, 0) + 1, Math.max(maxHistory, 1));
}

export function compactLuminance(luminance: number, maxLuminance = FIREFLY_LUMINANCE): number {
  if (!Number.isFinite(luminance) || luminance <= 0) return 0;
  return Math.min(luminance, maxLuminance);
}

export function normalizeAtrousIterations(value: number): number {
  const n = Math.max(1, Math.round(value));
  return n % 2 === 0 ? n + 1 : n;
}

export function isRestirDenoiserMode(value: string | null | undefined): value is RestirDenoiserMode {
  return (
    value !== null &&
    value !== undefined &&
    (RESTIR_DENOISER_MODES as readonly string[]).includes(value)
  );
}

/** 旧チェックボックスの `0` / `1` と未知値を含めて方式へ落とす。 */
export function resolveRestirDenoiserMode(value: string | null | undefined): RestirDenoiserMode {
  if (value === '0') return 'off';
  if (value === '1') return 'relax';
  if (isRestirDenoiserMode(value)) return value;
  return DEFAULT_RESTIR_DENOISER_MODE;
}

export function denoiserModeParams(mode: RestirDenoiserMode): RestirDenoiserModeParams {
  switch (mode) {
    case 'off':
    case 'temporal':
      return {
        atrous: false,
        feedbackFiltered: false,
        rectifySigma: 2.5,
        antiLag: 0,
        spatialFillOnMiss: 1,
        sigmaDepth: 1,
        sigmaNormal: 16,
        sigmaLumDiffuse: 4,
        sigmaLumSpecular: 2,
        maxStep: 1,
        historyBlurBoost: 0,
      };
    case 'svgf':
      return {
        atrous: true,
        feedbackFiltered: true,
        rectifySigma: 0,
        antiLag: 0,
        spatialFillOnMiss: 0,
        sigmaDepth: 1,
        sigmaNormal: 16,
        sigmaLumDiffuse: 4,
        sigmaLumSpecular: 2,
        maxStep: 99,
        historyBlurBoost: 0,
      };
    case 'svgf-sharp':
      return {
        atrous: true,
        feedbackFiltered: false,
        rectifySigma: 2,
        antiLag: 0,
        spatialFillOnMiss: 1,
        sigmaDepth: 0.6,
        sigmaNormal: 32,
        sigmaLumDiffuse: 2,
        sigmaLumSpecular: 1,
        maxStep: 2,
        historyBlurBoost: 0,
      };
    case 'relax':
      return {
        atrous: true,
        feedbackFiltered: false,
        rectifySigma: 3,
        antiLag: 1,
        spatialFillOnMiss: 1,
        sigmaDepth: 1,
        sigmaNormal: 24,
        sigmaLumDiffuse: 4,
        sigmaLumSpecular: 2,
        maxStep: 4,
        historyBlurBoost: 1,
      };
  }
}

export function rectifyLuminance(input: {
  history: number;
  neighborhoodMin: number;
  neighborhoodMax: number;
  mean: number;
  variance: number;
  sigma: number;
}): number {
  if (input.sigma <= 0) {
    const pad = 0.25 * Math.max(input.neighborhoodMax - input.neighborhoodMin, 0) + 1e-4;
    return Math.min(
      Math.max(input.history, Math.max(input.neighborhoodMin - pad, 0)),
      input.neighborhoodMax + pad
    );
  }
  const spread = Math.sqrt(Math.max(input.variance, 0)) * input.sigma;
  return Math.min(Math.max(input.history, Math.max(input.mean - spread, 0)), input.mean + spread);
}

export function antiLagHistoryScale(
  current: number,
  historyMean: number,
  historyVariance: number
): number {
  const sigma = Math.sqrt(Math.max(historyVariance, 1e-6));
  const lag = Math.abs(current - historyMean) / (2.5 * sigma + 1e-3);
  if (lag <= 1) return 1;
  return 1 / (1 + lag);
}

export function historyBlurBoostFactor(historyLength: number, boost: number): number {
  const conf = Math.min(Math.max((historyLength - 1) / 8, 0), 1);
  return 1 + 3 * boost * (1 - conf);
}

export function atrousStepSize(iteration: number, maxStep: number): number {
  return Math.min(2 ** Math.max(iteration, 0), Math.max(maxStep, 1));
}

export function geometryHistoryValid(input: {
  previousUv: readonly [number, number];
  positionDistance: number;
  normalDot: number;
  currentSurface: boolean;
  previousSurface: boolean;
  viewZ: number;
  voxelSize: number;
}): boolean {
  const [u, v] = input.previousUv;
  if (u <= 0 || v <= 0 || u >= 1 || v >= 1) return false;
  if (!input.currentSurface || input.currentSurface !== input.previousSurface) return false;
  if (input.normalDot < HISTORY_NORMAL_DOT_MIN) return false;
  const limit = Math.max(input.voxelSize * HISTORY_POS_VOXEL_SCALE, Math.abs(input.viewZ) * HISTORY_POS_VIEW_SCALE);
  return input.positionDistance < limit;
}

function temporalFrag(): string {
  return /* glsl */ `
in vec2 vUv;
layout(location = 0) out vec4 diffuseOut;
layout(location = 1) out vec4 specularOut;
layout(location = 2) out vec4 momentsOut;
layout(location = 3) out vec4 varianceOut;

${DENOISE_COMMON}

uniform sampler2D noisyDiffuse;
uniform sampler2D noisySpecular;
uniform sampler2D previousDiffuse;
uniform sampler2D previousSpecular;
uniform sampler2D previousMoments;
uniform mat4 previousViewProjection;
uniform mat4 currentViewProjection;
uniform float rectifySigma;
uniform float antiLag;
uniform float spatialFillOnMiss;

bool gatherHistory(
	vec2 prevUv, vec3 p, vec3 n, float viewZ,
	out vec3 histD, out vec3 histS, out vec4 histM, out float histNd, out float histNs
) {
	vec2 coord = prevUv / texel - 0.5;
	vec2 f = fract(coord);
	vec2 i0 = floor(coord);
	vec2 offs[4] = vec2[](vec2(0.0), vec2(1.0, 0.0), vec2(0.0, 1.0), vec2(1.0));
	float bw[4] = float[]((1.0 - f.x) * (1.0 - f.y), f.x * (1.0 - f.y), (1.0 - f.x) * f.y, f.x * f.y);
	vec3 dSum = vec3(0.0);
	vec3 sSum = vec3(0.0);
	vec4 mSum = vec4(0.0);
	float ndSum = 0.0;
	float nsSum = 0.0;
	float wSum = 0.0;
	for (int i = 0; i < 4; i++) {
		vec2 uv = (i0 + offs[i] + 0.5) * texel;
		if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) continue;
		vec4 tapPos = texture(previousPosition, uv);
		vec3 tapN = texture(previousNormal, uv).xyz;
		if (!geometryConsistent(p, n, tapPos, tapN, viewZ)) continue;
		float w = bw[i];
		vec4 pd = texture(previousDiffuse, uv);
		vec4 ps = texture(previousSpecular, uv);
		dSum += pd.rgb * w;
		sSum += ps.rgb * w;
		mSum += texture(previousMoments, uv) * w;
		ndSum += pd.a * w;
		nsSum += ps.a * w;
		wSum += w;
	}
	if (wSum < 0.02) return false;
	histD = dSum / wSum;
	histS = sSum / wSum;
	histM = mSum / wSum;
	histNd = ndSum / wSum;
	histNs = nsSum / wSum;
	return true;
}

void neighborhoodStats(
	vec3 p, vec3 n, float viewZ,
	out float dMin, out float dMax, out float sMin, out float sMax,
	out vec4 spatialMoments, out vec2 spatialVar, out vec3 spatialD, out vec3 spatialS
) {
	dMin = 1e20;
	dMax = 0.0;
	sMin = 1e20;
	sMax = 0.0;
	vec4 mSum = vec4(0.0);
	vec3 dSum = vec3(0.0);
	vec3 sSum = vec3(0.0);
	float count = 0.0;
	for (int y = -1; y <= 1; y++) {
		for (int x = -1; x <= 1; x++) {
			vec2 uv = vUv + vec2(float(x), float(y)) * texel;
			if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) continue;
			vec4 tapPos = texture(currentPosition, uv);
			vec3 tapN = texture(currentNormal, uv).xyz;
			if (!geometryConsistent(p, n, tapPos, tapN, viewZ)) continue;
			vec3 d = texture(noisyDiffuse, uv).rgb;
			vec3 s = texture(noisySpecular, uv).rgb;
			float ld = lightLuminance(d);
			float ls = lightLuminance(s);
			dMin = min(dMin, ld);
			dMax = max(dMax, ld);
			sMin = min(sMin, ls);
			sMax = max(sMax, ls);
			mSum += vec4(ld, ld * ld, ls, ls * ls);
			dSum += d;
			sSum += s;
			count += 1.0;
		}
	}
	if (count < 1.0) {
		vec3 d = texture(noisyDiffuse, vUv).rgb;
		vec3 s = texture(noisySpecular, vUv).rgb;
		float ld = lightLuminance(d);
		float ls = lightLuminance(s);
		dMin = ld;
		dMax = ld;
		sMin = ls;
		sMax = ls;
		spatialMoments = vec4(ld, ld * ld, ls, ls * ls);
		spatialVar = vec2(0.0);
		spatialD = d;
		spatialS = s;
		return;
	}
	spatialMoments = mSum / count;
	spatialVar = varianceFromMoments(spatialMoments);
	spatialD = dSum / count;
	spatialS = sSum / count;
}

void main() {
	vec4 pos = texture(currentPosition, vUv);
	if (pos.w < 0.5) {
		diffuseOut = vec4(0.0);
		specularOut = vec4(0.0);
		momentsOut = vec4(0.0);
		varianceOut = vec4(0.0);
		return;
	}

	vec3 p = pos.xyz;
	vec3 n = texture(currentNormal, vUv).xyz;
	float viewZ = viewZOf(p);
	vec3 curD = texture(noisyDiffuse, vUv).rgb;
	vec3 curS = texture(noisySpecular, vUv).rgb;
	float curLd = lightLuminance(curD);
	float curLs = lightLuminance(curS);

	float dMin;
	float dMax;
	float sMin;
	float sMax;
	vec4 spatialMoments;
	vec2 spatialVar;
	vec3 spatialD;
	vec3 spatialS;
	neighborhoodStats(p, n, viewZ, dMin, dMax, sMin, sMax, spatialMoments, spatialVar, spatialD, spatialS);

	vec3 histD;
	vec3 histS;
	vec4 histM;
	float histNd;
	float histNs;
	bool histOk = false;
	if (historyValid > 0.5) {
		vec4 prevClip = previousViewProjection * vec4(p, 1.0);
		vec4 currClip = currentViewProjection * vec4(p, 1.0);
		if (prevClip.w > 1e-4 && currClip.w > 1e-4) {
			vec2 prevUv = vUv + (prevClip.xy / prevClip.w - currClip.xy / currClip.w) * 0.5;
			histOk = gatherHistory(prevUv, p, n, viewZ, histD, histS, histM, histNd, histNs);
		}
	}

	vec3 outD;
	vec3 outS;
	vec4 outM;
	float outNd;
	float outNs;
	if (histOk) {
		histD = rectifyColor(histD, dMin, dMax, spatialMoments.x, spatialVar.x, rectifySigma);
		histS = rectifyColor(histS, sMin, sMax, spatialMoments.z, spatialVar.y, rectifySigma);
		outNd = min(histNd + 1.0, DIFFUSE_MAX_HISTORY);
		outNs = min(histNs + 1.0, SPECULAR_MAX_HISTORY);
		if (antiLag > 0.5) {
			outNd = max(mix(1.0, outNd, antiLagScale(curLd, histM.x, max(histM.y - histM.x * histM.x, 0.0))), 1.0);
			outNs = max(mix(1.0, outNs, antiLagScale(curLs, histM.z, max(histM.w - histM.z * histM.z, 0.0))), 1.0);
		}
		float ad = 1.0 / max(outNd, 1.0);
		float as = 1.0 / max(outNs, 1.0);
		outD = mix(histD, curD, ad);
		outS = mix(histS, curS, as);
		outM = vec4(
			mix(histM.x, curLd, ad),
			mix(histM.y, curLd * curLd, ad),
			mix(histM.z, curLs, as),
			mix(histM.w, curLs * curLs, as)
		);
	} else {
		outNd = 1.0;
		outNs = 1.0;
		outD = spatialFillOnMiss > 0.5 ? spatialD : curD;
		outS = spatialFillOnMiss > 0.5 ? spatialS : curS;
		outM = spatialMoments;
	}

	vec2 var = varianceFromMoments(outM);
	if (outNd < 4.0) var.x = max(var.x, spatialVar.x);
	if (outNs < 4.0) var.y = max(var.y, spatialVar.y);

	diffuseOut = vec4(outD, outNd);
	specularOut = vec4(outS, outNs);
	momentsOut = outM;
	varianceOut = vec4(var, 0.0, 1.0);
}
`;
}

function varianceFrag(): string {
  return /* glsl */ `
in vec2 vUv;
out vec4 fragColor;

${DENOISE_COMMON}

uniform sampler2D varianceMap;

void main() {
	vec4 pos = texture(currentPosition, vUv);
	if (pos.w < 0.5) {
		fragColor = vec4(0.0);
		return;
	}
	vec3 p = pos.xyz;
	vec3 n = texture(currentNormal, vUv).xyz;
	float viewZ = viewZOf(p);
	vec2 sum = vec2(0.0);
	float wSum = 0.0;
	for (int y = -1; y <= 1; y++) {
		for (int x = -1; x <= 1; x++) {
			vec2 uv = vUv + vec2(float(x), float(y)) * texel;
			if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) continue;
			vec4 tapPos = texture(currentPosition, uv);
			vec3 tapN = texture(currentNormal, uv).xyz;
			if (!geometryConsistent(p, n, tapPos, tapN, viewZ)) continue;
			float w = (x == 0 && y == 0) ? 4.0 : ((x == 0 || y == 0) ? 2.0 : 1.0);
			sum += texture(varianceMap, uv).rg * w;
			wSum += w;
		}
	}
	fragColor = vec4(sum / max(wSum, 1e-4), 0.0, 1.0);
}
`;
}

function atrousFrag(): string {
  return /* glsl */ `
in vec2 vUv;
layout(location = 0) out vec4 diffuseOut;
layout(location = 1) out vec4 specularOut;
layout(location = 2) out vec4 momentsOut;
layout(location = 3) out vec4 varianceOut;

${DENOISE_COMMON}

uniform sampler2D diffuseMap;
uniform sampler2D specularMap;
uniform sampler2D momentsMap;
uniform sampler2D varianceMap;
uniform float stepSize;
uniform float sigmaDepth;
uniform float sigmaNormal;
uniform float sigmaLumDiffuse;
uniform float sigmaLumSpecular;
uniform float historyBlurBoost;

void main() {
	vec4 centerPos = texture(currentPosition, vUv);
	vec4 centerD = texture(diffuseMap, vUv);
	vec4 centerS = texture(specularMap, vUv);
	if (centerPos.w < 0.5) {
		diffuseOut = vec4(0.0);
		specularOut = vec4(0.0);
		momentsOut = vec4(0.0);
		varianceOut = vec4(0.0);
		return;
	}

	vec3 centerN = texture(currentNormal, vUv).xyz;
	float centerZ = viewZOf(centerPos.xyz);
	vec2 centerVar = texture(varianceMap, vUv).rg;
	float centerLd = lightLuminance(centerD.rgb);
	float centerLs = lightLuminance(centerS.rgb);
	vec4 albedoMetal = texture(albedoMetalMap, vUv);
	float centerRough = texture(normalRoughMap, vUv).a;
	float centerMetal = max(albedoMetal.a, 0.0);
	float zGrad = max(max(abs(dFdx(centerZ)), abs(dFdy(centerZ))), 1e-4);
	float histLen = max(centerD.a, 1.0);
	float conf = clamp((histLen - 1.0) / 8.0, 0.0, 1.0);
	float lumBoost = mix(1.0 + 3.0 * historyBlurBoost, 1.0, conf);
	float specBoost = mix(1.0 + 2.0 * historyBlurBoost, 1.0, conf);
	float phiZ = sigmaDepth * max(zGrad * stepSize, abs(centerZ) * 0.002) + 1e-3;
	float phiD = sigmaLumDiffuse * sqrt(max(centerVar.x, 0.0)) * lumBoost + 1e-4;
	float phiS = sigmaLumSpecular * sqrt(max(centerVar.y, 0.0)) * specBoost + 1e-4;
	phiD = max(phiD, mix(0.15 * historyBlurBoost, 0.0, conf) * max(centerLd, 0.05));
	phiS = max(phiS, mix(0.08 * historyBlurBoost, 0.0, conf) * max(centerLs, 0.05));
	const float kernel[5] = float[](1.0 / 16.0, 4.0 / 16.0, 6.0 / 16.0, 4.0 / 16.0, 1.0 / 16.0);

	vec3 sumD = vec3(0.0);
	vec3 sumS = vec3(0.0);
	vec2 sumVar = vec2(0.0);
	float wDSum = 0.0;
	float wSSum = 0.0;

	for (int y = 0; y < 5; y++) {
		for (int x = 0; x < 5; x++) {
			vec2 uv = vUv + vec2(float(x) - 2.0, float(y) - 2.0) * stepSize * texel;
			if (any(lessThanEqual(uv, vec2(0.0))) || any(greaterThanEqual(uv, vec2(1.0)))) continue;
			vec4 tapPos = texture(currentPosition, uv);
			if (tapPos.w < 0.5) continue;
			vec3 tapN = texture(currentNormal, uv).xyz;
			float k = kernel[x] * kernel[y];
			float tapZ = viewZOf(tapPos.xyz);
			float wZ = exp(-abs(centerZ - tapZ) / phiZ);
			float ndot = max(dot(centerN, tapN), 0.0);
			float wN = pow(ndot, sigmaNormal);
			vec3 tapD = texture(diffuseMap, uv).rgb;
			vec3 tapS = texture(specularMap, uv).rgb;
			float wLd = exp(-abs(centerLd - lightLuminance(tapD)) / phiD);
			float wLs = exp(-abs(centerLs - lightLuminance(tapS)) / phiS);
			float tapRough = texture(normalRoughMap, uv).a;
			float tapMetal = max(texture(albedoMetalMap, uv).a, 0.0);
			float wMat = exp(-abs(centerRough - tapRough) * 8.0) * exp(-abs(centerMetal - tapMetal) * 8.0);
			float wD = k * wZ * wN * wLd;
			float wS = k * wZ * pow(ndot, sigmaNormal * 1.35) * wLs * wMat;
			sumD += tapD * wD;
			sumS += tapS * wS;
			vec2 tapVar = texture(varianceMap, uv).rg;
			sumVar += tapVar * vec2(wD * wD, wS * wS);
			wDSum += wD;
			wSSum += wS;
		}
	}

	vec3 outD = wDSum > 1e-6 ? sumD / wDSum : centerD.rgb;
	vec3 outS = wSSum > 1e-6 ? sumS / wSSum : centerS.rgb;
	vec2 outVar = vec2(
		wDSum > 1e-6 ? sumVar.x / max(wDSum * wDSum, 1e-8) : centerVar.x,
		wSSum > 1e-6 ? sumVar.y / max(wSSum * wSSum, 1e-8) : centerVar.y
	);
	diffuseOut = vec4(outD, centerD.a);
	specularOut = vec4(outS, centerS.a);
	momentsOut = texture(momentsMap, vUv);
	varianceOut = vec4(outVar, 0.0, 1.0);
}
`;
}

export interface RestirDenoiserInput {
  camera: THREE.PerspectiveCamera;
  gbuffer: GBuffer;
  currentReservoir: THREE.WebGLRenderTarget;
  previousReservoir: THREE.WebGLRenderTarget;
  previousViewProjection: THREE.Matrix4;
  noisyDiffuse: THREE.Texture;
  noisySpecular: THREE.Texture;
  sdfVoxelSize: number;
  historyValid: boolean;
}

export class RestirDenoiser {
  debugView: RestirDenoiserDebugView = 'off';
  lastCpuMs = { temporal: 0, variance: 0, atrous: 0, total: 0 };

  private readonly renderer: THREE.WebGLRenderer;
  private readonly histories: THREE.WebGLRenderTarget[];
  private readonly spatialTargets: THREE.WebGLRenderTarget[];
  private readonly variancePre: THREE.WebGLRenderTarget;
  private readonly temporalMaterial: THREE.ShaderMaterial;
  private readonly varianceMaterial: THREE.ShaderMaterial;
  private readonly atrousMaterial: THREE.ShaderMaterial;
  private readonly quad: FullScreenQuad;
  private readIndex = 0;
  private spatialRead = 0;
  private temporalIndex = 0;
  private historyValid = false;
  private displaySpatial = false;
  private iterations = 3;
  private _mode: RestirDenoiserMode = DEFAULT_RESTIR_DENOISER_MODE;
  private readonly size = new THREE.Vector2(1, 1);

  constructor(renderer: THREE.WebGLRenderer) {
    this.renderer = renderer;
    const gl = renderer.getContext() as WebGL2RenderingContext;
    const maxAttachments = gl.getParameter(gl.MAX_COLOR_ATTACHMENTS) as number;
    if (maxAttachments < 4) {
      console.warn(
        `ReSTIR denoiser expects 4 color attachments; this GPU reports ${maxAttachments}`
      );
    }
    this.histories = [this.makeHistoryTarget(), this.makeHistoryTarget()];
    this.spatialTargets = [this.makeHistoryTarget(), this.makeHistoryTarget()];
    this.variancePre = this.makeVarianceTarget();
    this.temporalMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: this.makeSharedUniforms({
        noisyDiffuse: { value: null },
        noisySpecular: { value: null },
        previousDiffuse: { value: null },
        previousSpecular: { value: null },
        previousMoments: { value: null },
        previousViewProjection: { value: new THREE.Matrix4() },
        currentViewProjection: { value: new THREE.Matrix4() },
        rectifySigma: { value: 0 },
        antiLag: { value: 0 },
        spatialFillOnMiss: { value: 0 },
      }),
      vertexShader: VERT,
      fragmentShader: temporalFrag(),
    });
    this.varianceMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: this.makeSharedUniforms({
        varianceMap: { value: null },
      }),
      vertexShader: VERT,
      fragmentShader: varianceFrag(),
    });
    this.atrousMaterial = new THREE.ShaderMaterial({
      glslVersion: THREE.GLSL3,
      depthTest: false,
      depthWrite: false,
      toneMapped: false,
      uniforms: this.makeSharedUniforms({
        diffuseMap: { value: null },
        specularMap: { value: null },
        momentsMap: { value: null },
        varianceMap: { value: null },
        stepSize: { value: 1 },
        sigmaDepth: { value: 1 },
        sigmaNormal: { value: 16 },
        sigmaLumDiffuse: { value: 4 },
        sigmaLumSpecular: { value: 2 },
        historyBlurBoost: { value: 0 },
      }),
      vertexShader: VERT,
      fragmentShader: atrousFrag(),
    });
    this.quad = new FullScreenQuad(this.temporalMaterial);
  }

  get mode(): RestirDenoiserMode {
    return this._mode;
  }

  set mode(value: RestirDenoiserMode) {
    const next = resolveRestirDenoiserMode(value);
    if (this._mode === next) return;
    this._mode = next;
    this.resetHistory();
  }

  applyQuality(quality: GraphicsQuality): void {
    this.iterations = normalizeAtrousIterations(quality.denoiserAtrousIterations);
    this.resetHistory();
  }

  setSize(width: number, height: number): void {
    const w = Math.max(1, Math.floor(width));
    const h = Math.max(1, Math.floor(height));
    if (this.size.x === w && this.size.y === h) {
      this.resetHistory();
      return;
    }
    this.size.set(w, h);
    for (const target of this.histories) target.setSize(w, h);
    for (const target of this.spatialTargets) target.setSize(w, h);
    this.variancePre.setSize(w, h);
    const texel = new THREE.Vector2(1 / w, 1 / h);
    this.temporalMaterial.uniforms.texel.value.copy(texel);
    this.varianceMaterial.uniforms.texel.value.copy(texel);
    this.atrousMaterial.uniforms.texel.value.copy(texel);
    this.resetHistory();
  }

  resetHistory(): void {
    this.historyValid = false;
  }

  get diffuse(): THREE.Texture {
    return this.displayTarget().textures[0];
  }

  get specular(): THREE.Texture {
    return this.displayTarget().textures[1];
  }

  get variance(): THREE.Texture {
    return this.displayTarget().textures[3];
  }

  get temporalDiffuse(): THREE.Texture {
    return this.histories[this.temporalIndex].textures[0];
  }

  get temporalSpecular(): THREE.Texture {
    return this.histories[this.temporalIndex].textures[1];
  }

  render(input: RestirDenoiserInput): void {
    if (this._mode === 'off') return;
    const params = denoiserModeParams(this._mode);
    const t0 = performance.now();
    const writeIndex = 1 - this.readIndex;
    this.bindCommon(input);
    this.bindModeParams(params);
    this.renderTemporal(input, writeIndex);
    const t1 = performance.now();
    this.temporalIndex = writeIndex;
    const skipAtrous = !params.atrous || this.debugView === 'temporal';
    if (skipAtrous) {
      this.readIndex = writeIndex;
      this.displaySpatial = false;
      this.historyValid = true;
      this.lastCpuMs = { temporal: t1 - t0, variance: 0, atrous: 0, total: t1 - t0 };
      return;
    }
    this.renderVariancePrefilter(writeIndex);
    const t2 = performance.now();
    this.renderAtrous(writeIndex, params);
    const t3 = performance.now();
    this.historyValid = true;
    this.lastCpuMs = {
      temporal: t1 - t0,
      variance: t2 - t1,
      atrous: t3 - t2,
      total: t3 - t0,
    };
  }

  dispose(): void {
    for (const target of this.histories) target.dispose();
    for (const target of this.spatialTargets) target.dispose();
    this.variancePre.dispose();
    this.temporalMaterial.dispose();
    this.varianceMaterial.dispose();
    this.atrousMaterial.dispose();
    this.quad.dispose();
  }

  private displayTarget(): THREE.WebGLRenderTarget {
    return this.displaySpatial ? this.spatialTargets[this.spatialRead] : this.histories[this.readIndex];
  }

  private bindModeParams(params: RestirDenoiserModeParams): void {
    const tu = this.temporalMaterial.uniforms;
    tu.rectifySigma.value = params.rectifySigma;
    tu.antiLag.value = params.antiLag;
    tu.spatialFillOnMiss.value = params.spatialFillOnMiss;
    const au = this.atrousMaterial.uniforms;
    au.sigmaDepth.value = params.sigmaDepth;
    au.sigmaNormal.value = params.sigmaNormal;
    au.sigmaLumDiffuse.value = params.sigmaLumDiffuse;
    au.sigmaLumSpecular.value = params.sigmaLumSpecular;
    au.historyBlurBoost.value = params.historyBlurBoost;
  }

  private renderTemporal(input: RestirDenoiserInput, writeIndex: number): void {
    const previous = this.histories[this.readIndex];
    const u = this.temporalMaterial.uniforms;
    u.noisyDiffuse.value = input.noisyDiffuse;
    u.noisySpecular.value = input.noisySpecular;
    u.previousDiffuse.value = previous.textures[0];
    u.previousSpecular.value = previous.textures[1];
    u.previousMoments.value = previous.textures[2];
    u.previousViewProjection.value.copy(input.previousViewProjection);
    u.currentViewProjection.value.multiplyMatrices(
      input.camera.projectionMatrix,
      input.camera.matrixWorldInverse
    );
    u.historyValid.value = this.historyValid && input.historyValid ? 1 : 0;
    this.draw(this.temporalMaterial, this.histories[writeIndex]);
  }

  private renderVariancePrefilter(writeIndex: number): void {
    const source = this.histories[writeIndex];
    this.varianceMaterial.uniforms.varianceMap.value = source.textures[3];
    this.draw(this.varianceMaterial, this.variancePre);
  }

  private renderAtrous(writeIndex: number, params: RestirDenoiserModeParams): void {
    let varianceMap: THREE.Texture = this.variancePre.texture;
    if (params.feedbackFiltered) {
      let colorRead = writeIndex;
      for (let i = 0; i < this.iterations; i++) {
        const colorWrite = 1 - colorRead;
        this.drawAtrous(this.histories[colorRead], this.histories[colorWrite], varianceMap, i, params);
        varianceMap = this.histories[colorWrite].textures[3];
        colorRead = colorWrite;
      }
      this.readIndex = colorRead;
      this.displaySpatial = false;
      return;
    }

    let src = this.histories[writeIndex];
    let colorRead = 0;
    for (let i = 0; i < this.iterations; i++) {
      const colorWrite = i === 0 ? 0 : 1 - colorRead;
      const dst = this.spatialTargets[colorWrite];
      this.drawAtrous(src, dst, varianceMap, i, params);
      varianceMap = dst.textures[3];
      src = dst;
      colorRead = colorWrite;
    }
    this.readIndex = writeIndex;
    this.spatialRead = colorRead;
    this.displaySpatial = true;
  }

  private drawAtrous(
    src: THREE.WebGLRenderTarget,
    dst: THREE.WebGLRenderTarget,
    varianceMap: THREE.Texture,
    iteration: number,
    params: RestirDenoiserModeParams
  ): void {
    const u = this.atrousMaterial.uniforms;
    u.diffuseMap.value = src.textures[0];
    u.specularMap.value = src.textures[1];
    u.momentsMap.value = src.textures[2];
    u.varianceMap.value = varianceMap;
    u.stepSize.value = atrousStepSize(iteration, params.maxStep);
    this.draw(this.atrousMaterial, dst);
  }

  private bindCommon(input: RestirDenoiserInput): void {
    for (const material of [this.temporalMaterial, this.varianceMaterial, this.atrousMaterial]) {
      const u = material.uniforms;
      u.depthMap.value = input.gbuffer.depth;
      u.albedoMetalMap.value = input.gbuffer.albedoMetal;
      u.normalRoughMap.value = input.gbuffer.normalRough;
      u.currentPosition.value = input.currentReservoir.textures[1];
      u.currentNormal.value = input.currentReservoir.textures[2];
      u.previousPosition.value = input.previousReservoir.textures[1];
      u.previousNormal.value = input.previousReservoir.textures[2];
      u.sceneViewMatrix.value.copy(input.camera.matrixWorldInverse);
      u.cameraPos.value.setFromMatrixPosition(input.camera.matrixWorld);
      u.sdfVoxelSize.value = input.sdfVoxelSize;
    }
  }

  private draw(material: THREE.ShaderMaterial, target: THREE.WebGLRenderTarget): void {
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = true;
    this.renderer.setRenderTarget(target);
    this.quad.material = material;
    this.quad.render(this.renderer);
    this.renderer.autoClear = previousAutoClear;
  }

  private makeSharedUniforms(
    extra: Record<string, THREE.IUniform>
  ): Record<string, THREE.IUniform> {
    return {
      depthMap: { value: null },
      albedoMetalMap: { value: null },
      normalRoughMap: { value: null },
      currentPosition: { value: null },
      currentNormal: { value: null },
      previousPosition: { value: null },
      previousNormal: { value: null },
      sceneViewMatrix: { value: new THREE.Matrix4() },
      cameraPos: { value: new THREE.Vector3() },
      texel: { value: new THREE.Vector2(1, 1) },
      sdfVoxelSize: { value: 0.25 },
      historyValid: { value: 0 },
      ...extra,
    };
  }

  private makeHistoryTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      count: 4,
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    for (const texture of target.textures) {
      texture.colorSpace = THREE.NoColorSpace;
      texture.generateMipmaps = false;
    }
    return target;
  }

  private makeVarianceTarget(): THREE.WebGLRenderTarget {
    const target = new THREE.WebGLRenderTarget(1, 1, {
      type: THREE.HalfFloatType,
      minFilter: THREE.NearestFilter,
      magFilter: THREE.NearestFilter,
      depthBuffer: false,
      stencilBuffer: false,
    });
    target.texture.colorSpace = THREE.NoColorSpace;
    target.texture.generateMipmaps = false;
    return target;
  }
}
